import { Children } from 'react';
import type { ComponentProps, ReactNode } from 'react';
import { Button as TamaguiButton, XStack, YStack, isWeb, styled } from 'tamagui';

import { AppCard, AppHeading, AppRow, AppSectionHeader, AppText } from './primitives';
import {
  getAppBottomSheetPresentationProps,
  getAppTabBarPresentationProps,
  getAppTabButtonPresentationProps,
} from './presentation';
import type { AppPlatformPresentation, AppSurfaceDensity } from './presentation';

export const AppShell = styled(YStack, {
  name: 'AppShell',
  flex: 1,
  width: '100%',
  backgroundColor: '$background',
});

export const AppShellMain = styled(YStack, {
  name: 'AppShellMain',
  flex: 1,
  width: '100%',
});

export const AppContentContainer = styled(YStack, {
  name: 'AppContentContainer',
  width: '100%',
  maxWidth: 960,
  alignSelf: 'center',
  px: '$md',
});

export const AppPageActions = styled(XStack, {
  name: 'AppPageActions',
  alignItems: 'center',
  gap: '$sm',
  flexWrap: 'wrap',
});

export const AppTabShell = styled(AppContentContainer, {
  name: 'AppTabShell',
  pt: '$sm',
  pb: '$xxl',
  gap: '$sm',
});

export type AppSectionHeroStat = {
  label: string;
  value: string;
  tone?: 'default' | 'accent' | 'success' | 'danger';
};

type AppHeaderBarProps = {
  title?: string;
  action?: any;
  topPadding?: number | string;
  bottomPadding?: number | string;
  leadingVisual?: any;
  titlePrefix?: any;
};

function WebAdultingMarkImage({
  imageWidth = 24,
  imageHeight = 21,
}: {
  imageWidth?: number;
  imageHeight?: number;
}) {
  if (!isWeb) {
    return null;
  }

  return (
    <img
      src="/adulting-icon.png"
      alt=""
      aria-hidden="true"
      style={{
        width: imageWidth,
        height: imageHeight,
        objectFit: 'contain',
        flexShrink: 0,
      }}
    />
  );
}

export function AppAdultingMarkTile({
  tileSize = 32,
  imageWidth = 24,
  imageHeight = 21,
}: {
  tileSize?: number;
  imageWidth?: number;
  imageHeight?: number;
}) {
  if (!isWeb) {
    return null;
  }

  return (
    <XStack
      width={tileSize}
      height={tileSize}
      alignItems="center"
      justifyContent="center"
      borderRadius={Math.round(tileSize * 0.32)}
      backgroundColor="rgba(255,255,255,0.96)"
      borderWidth={1}
      borderColor="rgba(136,146,160,0.22)"
      overflow="hidden"
      flexShrink={0}
    >
      <WebAdultingMarkImage imageWidth={imageWidth} imageHeight={imageHeight} />
    </XStack>
  );
}

function AppTitleContent({
  title,
  leadingVisual,
  color,
  titlePrefix,
}: {
  title?: string;
  leadingVisual?: any;
  color?: string;
  titlePrefix?: any;
}) {
  if (!title) {
    return <YStack flex={1} />;
  }

  const mark = leadingVisual ?? <AppAdultingMarkTile />;

  return (
    <XStack flex={1} alignItems="center" gap="$sm">
      {titlePrefix ? titlePrefix : null}
      {mark}
      <AppHeading flex={1} color={color} fontSize="$xxl" lineHeight="$xxl" letterSpacing={-0.2}>
        {title}
      </AppHeading>
    </XStack>
  );
}

export function AppHeaderBar({
  title,
  action,
  topPadding = '$xxl',
  bottomPadding = '$xs',
  leadingVisual,
  titlePrefix,
}: AppHeaderBarProps) {
  return (
    <AppContentContainer pt={topPadding} pb={bottomPadding}>
      <AppRow alignItems="flex-start">
        <AppTitleContent title={title} leadingVisual={leadingVisual} color="$primary" titlePrefix={titlePrefix} />
        {action ? <AppPageActions>{action}</AppPageActions> : null}
      </AppRow>
    </AppContentContainer>
  );
}

type AppPageIntroProps = {
  title?: string;
  subtitle?: string;
  action?: any;
  leadingVisual?: any;
  titlePrefix?: any;
};

export function AppPageIntro({ title, subtitle, action, leadingVisual, titlePrefix }: AppPageIntroProps) {
  if (!title && !subtitle && !action) {
    return null;
  }

  return (
    <YStack gap="$xs">
      {title || action ? (
        <AppRow alignItems="flex-start">
          <AppTitleContent title={title} leadingVisual={leadingVisual} titlePrefix={titlePrefix} />
          {action ? <AppPageActions>{action}</AppPageActions> : null}
        </AppRow>
      ) : null}
      {subtitle ? (
        <AppText fontSize="$sm" lineHeight="$md" color="$colorMuted" maxWidth={720}>
          {subtitle}
        </AppText>
      ) : null}
    </YStack>
  );
}

type AppSectionHeroProps = {
  title: string;
  subtitle?: string;
  action?: any;
  leadingVisual?: any;
  titlePrefix?: any;
  stats?: AppSectionHeroStat[];
  children?: any;
  tone?: 'default' | 'compact';
  presentation?: AppPlatformPresentation;
  isWideAppleLayout?: boolean;
  hideHeading?: boolean;
};

export function AppSectionHero({
  title,
  subtitle,
  action,
  leadingVisual,
  titlePrefix,
  stats: _stats = [],
  children,
  tone = 'default',
  presentation = 'shared',
  isWideAppleLayout = false,
  hideHeading = false,
}: AppSectionHeroProps) {
  const mark = leadingVisual ?? <WebAdultingMarkImage imageWidth={28} imageHeight={24} />;
  const isApple = presentation === 'apple';
  const isAndroid = presentation === 'android';
  const isCompact = tone === 'compact';
  const hasRenderableChildren = Children.toArray(children).length > 0;
  const suppressEmptyNativeHero = hideHeading && !subtitle && !action && !hasRenderableChildren;

  if (suppressEmptyNativeHero) {
    return null;
  }

  const heroBackgroundColor = isApple ? 'rgba(248,250,252,0.92)' : '$primary';
  const heroBorderColor = isApple ? 'rgba(136,146,160,0.22)' : 'transparent';
  const heroShadowColor = isApple ? 'rgba(24,32,43,0.12)' : '$shadowColor';
  const heroTitleColor = isApple ? '$primary' : '$primaryForeground';
  const heroSubtitleColor = isApple
    ? '$colorMuted'
    : hideHeading
      ? 'rgba(255,255,255,0.94)'
      : 'rgba(255,255,255,0.76)';
  const heroMarkBackgroundColor = isApple ? 'rgba(255,255,255,0.94)' : 'rgba(255,255,255,0.96)';
  const heroMarkBorderColor = isApple ? 'rgba(136,146,160,0.24)' : 'rgba(136,146,160,0.2)';
  const titleFontSize = isApple
    ? (isCompact ? (isWideAppleLayout ? 28 : 24) : (isWideAppleLayout ? 34 : 30))
    : (isCompact ? '$lg' : '$xl');
  const titleLineHeight = isApple
    ? (isCompact ? (isWideAppleLayout ? 32 : 29) : (isWideAppleLayout ? 38 : 34))
    : (isCompact ? 20 : 22);
  const subtitleLineHeight = isApple ? (isCompact ? 18 : 20) : (isCompact ? 15 : 16);

  if (isApple && hideHeading) {
    if (!subtitle && !action && !hasRenderableChildren) {
      return null;
    }

    return (
      <YStack gap="$sm">
        {action ? (
          <AppRow justifyContent="flex-end">
            <AppPageActions>{action}</AppPageActions>
          </AppRow>
        ) : null}
        {subtitle ? (
          <AppText
            color={heroSubtitleColor}
            fontSize="$sm"
            lineHeight={19}
            maxFontSizeMultiplier={1.22}
          >
            {subtitle}
          </AppText>
        ) : null}
        {children}
      </YStack>
    );
  }

  return (
    <YStack
      backgroundColor={heroBackgroundColor}
      borderWidth={1}
      borderColor={heroBorderColor}
      borderRadius={isApple ? (isCompact ? 24 : 28) : '$xl'}
      px={isApple ? (isCompact ? '$md' : '$lg') : (isCompact ? '$sm' : '$md')}
      py={isApple ? (isCompact ? 14 : 16) : (isCompact ? 10 : 12)}
      gap={isApple ? (isCompact ? '$xs' : '$sm') : '$xs'}
      shadowColor={heroShadowColor}
      shadowOffset={{ width: 0, height: 16 }}
      shadowOpacity={isCompact ? 0.12 : 0.18}
      shadowRadius={isCompact ? 22 : 30}
      elevation={4}
    >
      {isApple ? (
        <>
          {!hideHeading ? (
            <AppRow alignItems="center" gap="$sm">
              <XStack flex={1} alignItems="center" gap="$sm">
                {titlePrefix ? titlePrefix : null}
                <YStack
                  width={38}
                  height={38}
                  alignItems="center"
                  justifyContent="center"
                  borderRadius={19}
                  backgroundColor={heroMarkBackgroundColor}
                  borderWidth={1}
                  borderColor={heroMarkBorderColor}
                  overflow="hidden"
                >
                  {mark}
                </YStack>
                <AppText
                  flex={1}
                  color={heroTitleColor}
                  fontSize={titleFontSize}
                  lineHeight={titleLineHeight}
                  fontWeight="800"
                  letterSpacing={isCompact ? -0.5 : -0.8}
                  maxFontSizeMultiplier={1.28}
                >
                  {title}
                </AppText>
              </XStack>
              {action ? <AppPageActions>{action}</AppPageActions> : null}
            </AppRow>
          ) : action ? (
            <AppRow justifyContent="flex-end">
              <AppPageActions>{action}</AppPageActions>
            </AppRow>
          ) : null}

          <AppText
            color={heroSubtitleColor}
            fontSize="$sm"
            lineHeight={subtitleLineHeight}
            maxFontSizeMultiplier={1.22}
          >
            {subtitle}
          </AppText>
        </>
      ) : (
        <>
          {!hideHeading ? (
            <AppRow alignItems="flex-start" gap="$sm">
              <XStack flex={1} alignItems="center" gap="$sm">
                {titlePrefix ? titlePrefix : null}
                <YStack
                  width={36}
                  height={36}
                  alignItems="center"
                  justifyContent="center"
                  borderRadius="$lg"
                  backgroundColor="rgba(255,255,255,0.96)"
                  borderWidth={1}
                  borderColor="rgba(136,146,160,0.2)"
                  overflow="hidden"
                >
                  {mark}
                </YStack>
                <AppText
                  flex={1}
                  color="$primaryForeground"
                  fontSize="$xl"
                  lineHeight={22}
                  fontWeight="700"
                  letterSpacing={-0.2}
                >
                  {title}
                </AppText>
              </XStack>
              {action ? <AppPageActions>{action}</AppPageActions> : null}
            </AppRow>
          ) : action ? (
            <AppRow justifyContent="flex-end">
              <AppPageActions>{action}</AppPageActions>
            </AppRow>
          ) : null}

          {subtitle ? (
            <AppText color="rgba(255,255,255,0.68)" fontSize="$xs" lineHeight={16}>
              {subtitle}
            </AppText>
          ) : null}
        </>
      )}

      {hasRenderableChildren ? <YStack gap="$xs">{children}</YStack> : null}
    </YStack>
  );
}

export function AppExplanationCard({
  title,
  body,
  action,
}: {
  title: string;
  body: ReactNode;
  action?: ReactNode;
}) {
  return (
    <AppCard gap="$md">
      <AppSectionHeader title={title} />
      <AppText fontSize="$md" lineHeight="$lg" color="$color">
        {body}
      </AppText>
      {action ? <AppPageActions>{action}</AppPageActions> : null}
    </AppCard>
  );
}

type AppBottomSheetProps = {
  children: ReactNode;
  onClose?: () => void;
  surfaceClassName?: string;
  viewportClassName?: string;
  presentation?: AppPlatformPresentation;
};

export function AppBottomSheet({
  children,
  onClose,
  surfaceClassName,
  viewportClassName,
  presentation = 'shared',
}: AppBottomSheetProps) {
  return (
    <YStack position="fixed" t={0} l={0} r={0} b={0} zIndex="$modal" justifyContent="flex-end" className={viewportClassName}>
      <TamaguiButton
        unstyled
        type="button"
        aria-label="Close panel"
        position="absolute"
        t={0}
        l={0}
        r={0}
        b={0}
        backgroundColor="$overlayColor"
        className="animate-fade-in"
        onPress={onClose}
      />
      <YStack position="relative" className="animate-slide-up" px="$xs" $gtSm={{ px: 0 }}>
        <XStack justifyContent="center" pt="$xs" pb={4}>
          <YStack width={40} height={4} borderRadius="$pill" backgroundColor="$colorMuted" opacity={0.3} />
        </XStack>
        <AppCard
          presentation={presentation}
          borderBottomWidth={0}
          borderBottomLeftRadius={0}
          borderBottomRightRadius={0}
          maxHeight="85vh"
          className={surfaceClassName}
          {...getAppBottomSheetPresentationProps(presentation)}
        >
          {children}
        </AppCard>
      </YStack>
    </YStack>
  );
}

type AppBottomSheetHeaderProps = {
  title: string;
  subtitle?: string;
  action?: ReactNode;
  className?: string;
};

export function AppBottomSheetHeader({
  title,
  subtitle,
  action,
  className,
}: AppBottomSheetHeaderProps) {
  return (
    <YStack px="$md" pt="$xs" pb="$sm" borderBottomWidth={1} borderColor="$borderColor" className={className}>
      <AppSectionHeader title={title} subtitle={subtitle} action={action} />
    </YStack>
  );
}

const AppTabBarFrame = styled(XStack, {
  name: 'AppTabBar',
  width: '100%',
  alignItems: 'stretch',
  gap: 0,
  backgroundColor: '$cardBackground',
  borderBottomWidth: 1,
  borderColor: '$borderColor',
  px: '$xs',
});

type AppTabBarProps = ComponentProps<typeof AppTabBarFrame> & {
  presentation?: AppPlatformPresentation;
  density?: AppSurfaceDensity;
};

export function AppTabBar({
  presentation = 'shared',
  density = 'comfortable',
  ...props
}: AppTabBarProps) {
  return (
    <AppTabBarFrame
      borderWidth={presentation === 'apple' || presentation === 'android' ? 1 : 0}
      borderBottomWidth={1}
      {...getAppTabBarPresentationProps(presentation, density)}
      {...props}
    />
  );
}

type AppTabButtonProps = {
  active?: boolean;
  onPress: () => void;
  children: any;
  presentation?: AppPlatformPresentation;
  density?: AppSurfaceDensity;
};

export function AppTabButton({
  active = false,
  onPress,
  children,
  presentation = 'shared',
  density = 'comfortable',
}: AppTabButtonProps) {
  const presentationStyles = getAppTabButtonPresentationProps(presentation, density);
  const isApplePresentation = presentation === 'apple';
  const isAndroidPresentation = presentation === 'android';

  return (
    <TamaguiButton
      unstyled
      flex={1}
      alignItems="center"
      justifyContent="center"
      backgroundColor={active ? (isApplePresentation ? '$background' : isAndroidPresentation ? '$primaryTint' : 'transparent') : 'transparent'}
      borderWidth={isApplePresentation ? 1 : 0}
      borderColor={isApplePresentation && active ? '$borderColor' : 'transparent'}
      borderBottomWidth={isApplePresentation ? 1 : isAndroidPresentation ? 0 : 2.5}
      borderBottomColor={isApplePresentation || isAndroidPresentation ? 'transparent' : active ? '$primary' : 'transparent'}
      color={active ? (isApplePresentation ? '$color' : isAndroidPresentation ? '$primary' : '$primary') : '$colorMuted'}
      fontFamily="$body"
      fontSize="$sm"
      fontWeight="600"
      hoverStyle={active ? { opacity: 0.96 } : { color: '$color' }}
      pressStyle={{ opacity: 0.92 }}
      {...presentationStyles}
      onPress={onPress}
    >
      {children}
    </TamaguiButton>
  );
}
